/**
 * La cobertura del saldo en «Cuenta de Twilio»: si el saldo de Twilio alcanza para lo que cargaron en la plataforma
 * las marcas que pagan con esa cuenta (`coberturaSaldo` de `GET /estadisticas/twilio`; tono y textos en
 * logic/cobertura.ts).
 *
 * Cuando no alcanza, el aviso (ámbar, o rojo si Twilio ya está en el mínimo de la alerta de saldo) dice cuánto falta y
 * que una recarga en la plataforma NO carga plata en Twilio: se carga desde su consola o con la recarga automática.
 * Antes la tarjeta mostraba sólo el saldo de Twilio, y el de las marcas vivía en otra pantalla: nadie veía que
 * USD 59,36 cargados no entraban en USD 10,65.
 *
 * Es del equipo, como toda esta tarjeta: la cuenta de Twilio es de Deenex.
 */

import { AlertTriangle, CheckCircle2, ExternalLink, Info } from "lucide-react";
import type { CoberturaSaldo } from "../../types.estadisticas";
import { TEXTO_RECARGA_NO_CARGA_TWILIO, avisoCobertura, type TonoCobertura } from "../../logic/cobertura";
import { CONSOLA_TWILIO } from "../../logic/estadisticas";
import { usd } from "../../logic/formato";

const CAJA: Record<Exclude<TonoCobertura, "alcanza">, string> = {
  urgente: "border-red-200 bg-red-50 text-red-800",
  no_alcanza: "border-amber-200 bg-amber-50 text-amber-800",
  sin_comparar: "border-slate-200 bg-slate-50 text-slate-700",
};

export function AvisoCoberturaSaldo({ cobertura, saldoMinimoUSD }: { cobertura: CoberturaSaldo | null | undefined; saldoMinimoUSD?: number | null }) {
  const a = avisoCobertura(cobertura, saldoMinimoUSD);
  if (!a || !cobertura) return null;

  if (a.tono === "alcanza") {
    return (
      <p role="status" data-cobertura="alcanza" className="flex items-start gap-2 text-xs text-emerald-700">
        <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
        <span><strong className="font-semibold">{a.titulo}.</strong> {a.detalle}</span>
      </p>
    );
  }

  const Icono = a.tono === "sin_comparar" ? Info : AlertTriangle;
  return (
    <div role={a.tono === "sin_comparar" ? "status" : "alert"} data-cobertura={a.tono} className={`rounded-xl border px-4 py-3 text-sm ${CAJA[a.tono]}`}>
      <p className="flex items-start gap-2 font-semibold">
        <Icono size={15} className="flex-shrink-0 mt-0.5" /> {a.titulo}
      </p>
      <p className="mt-1">{a.detalle}</p>
      {cobertura.marcas.length > 1 && (
        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Lo que cargó cada marca">
          {cobertura.marcas.map((m) => (
            <li key={m.brandId} className="rounded-md bg-white/70 px-2 py-0.5 text-xs tabular-nums">
              {m.brandId} · {usd(m.saldoUSD)}
            </li>
          ))}
        </ul>
      )}
      {a.explicarRecarga && (
        <p className="mt-2">
          {TEXTO_RECARGA_NO_CARGA_TWILIO}{" "}
          <a href={CONSOLA_TWILIO} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline">
            Abrir la consola de Twilio <ExternalLink size={12} />
          </a>
        </p>
      )}
    </div>
  );
}

export default AvisoCoberturaSaldo;
